import React from 'react';
import { useTranslation } from 'react-i18next';
//import { useNavigate } from 'react-router-dom';
import { Skeleton } from "@heroui/react";

import { RoleContext } from '@/app/api/common';
import { store, useAppSelector } from '@/app/store';
import { checkNoError, FieldDescriptor, getField, setField } from '@/features/channels/channelsSlice';
import { LoginDescriptor } from '@/features/logins/loginsSlice';
import { copyProjectInfo, copyProjectLimits, copyProjectRoom, HotelInfo, HotelLimits, HotelRoom } from '@/features/projects/projectsSlice';

import Button from '@/components/Common/Button';
import CheckBox from '@/components/Common/CheckBox';
import Dropdown from '@/components/Common/Dropdown';
import { FieldInfo } from '@/components/Common/Dropdown';
import { WelField } from '@/components/Common/Field';
import FlexRow from '@/components/Common/FlexRow';
import Input, { TextArea } from '@/components/Common/Input'

import { CatHeader } from './Project';

export interface ProjectThemeProps {
  project:				HotelInfo;
  setProject:			(val: HotelInfo) => any;
  limits:				HotelLimits | null;
  setLimits:			(lim: HotelLimits) => any;
  room?:				HotelRoom;
  setRoom?:				(room: HotelRoom) => any;
}

const ProjectTheme = (props: ProjectThemeProps) => {
  const { t } = useTranslation();
  const role = React.useContext(RoleContext);
  const logins = useAppSelector((state) => state.logins);
  const isProjectMember = React.useMemo(() => {
    const username = logins.login?.username;
    if (!username || !props.project) return false;
    const localProjectId = logins.login?.local_project_id;
    if (localProjectId && localProjectId === props.project.id) return true;

    if (props.project.owner === username) return true;
    if (!props.limits) return false;
    const execs = props.limits.executives;
    return execs?.partner?.includes(username) ||
           execs?.chain_manager?.includes(username) ||
           execs?.manager?.includes(username);
  }, [logins.login, props.project, props.limits]);
  const seeAllOnly = role?.privilages?.includes("SeeAllProjects") && !role.admin && !role?.privilages?.includes("EditProjectSettings") && !isProjectMember;
  const [themeOpts, setThemeOpts] = React.useState([] as FieldInfo[]);
  
  const loading =	!props.project.is_new &&
			  		props.project.id != '' &&
			  		!props.limits;
  
  React.useEffect(() => {
    const opts = [
      {	value:	"|",				desc:	t("projects:project:theme:select_theme:|") },
      {	value:	"one|",				desc:	t("projects:project:theme:select_theme:one|") },
      //{	value:	"one|one-two",		desc:	t("projects:project:theme:select_theme:one|one-two") },
      {	value:	"two|two-one",		desc:	t("projects:project:theme:select_theme:two|two-one") },
      {	value:	"two|two-four",		desc:	t("projects:project:theme:select_theme:two|two-four") },
      {	value:	"two|two-two",		desc:	t("projects:project:theme:select_theme:two|two-two") },
      //{	value:	"two|two-three",	desc:	t("projects:project:theme:select_theme:two|two-three") },
    ];
    
    if (props.room)
      opts.push({	value:	"default",		desc:	t("projects:project:theme:select_theme:default") });
    
    setThemeOpts(opts);
  }, [t, props.room]);
  
  const themeVal = React.useMemo((): string => {
    if (props.room) {
      if (!props.room.fields)
        return "";
      
      let val = getField(props.room.fields, "theme");
      if (val == "")
        val = "default";
      
      return val;
    }
    
    return	props.limits &&
			props.limits.welcome &&
			props.limits.welcome.theme
			?	props.limits.welcome.theme.active + "|" + props.limits.welcome.theme.variant
			:	"";
  }, [props.limits, props.room]);
  
  const message = React.useMemo((): string => {
    if (props.room) {
      if (!props.room.fields)
        return "";
      
      return getField(props.room.fields, "theme-message");
    }
    
    return	props.limits &&
			props.limits.welcome
			?	props.limits.welcome.message
			:	"";
  }, [props.limits, props.room]);
  
  const description = React.useMemo((): string => {
    if (props.room) {
      if (!props.room.fields)
        return "";
      
      return getField(props.room.fields, "theme-description");
    }
    
    return	props.limits &&
			props.limits.welcome
			?	props.limits.welcome.description
			:	"";
  }, [props.limits, props.room]);
  
  const footer = React.useMemo((): string => {
    if (props.room) {
      if (!props.room.fields)
        return "";
      
      return getField(props.room.fields, "theme-footer");
    }
    
    return	props.limits &&
			props.limits.welcome
			?	props.limits.welcome.footer
			:	"";
  }, [props.limits, props.room]);
  
  const updateTheme = React.useCallback((val: string) => {
    if (val == "default")
      val = "";
    
    const valArr = val.split("|");
    while (valArr.length < 2) {
      valArr.push("");
    }
    
    if (props.room) {
      if (!props.setRoom)
        return;
        
      let room = copyProjectRoom(props.room);
      if (!room.fields)
        room.fields = [];
      
      room.fields = setField(room.fields, "theme", val);
      
      props.setRoom(room);
      console.log("ProjectTheme/updateTheme/val=" + val + "/valArr=", valArr, "/room=", room);
      return;
    }
    
    if (	!props.limits ||
		   	!props.limits.welcome ||
		   	!props.limits.welcome.theme
	   )
      return;
      
    let limits = copyProjectLimits(props.limits);
    
    limits.welcome.theme.active = valArr[0];
    limits.welcome.theme.variant = valArr[1];
    
    console.log("ProjectTheme/updateTheme/val=" + val + "/valArr=", valArr, "/limits=", limits);
    
    props.setLimits(limits);
  }, [props.limits, props.room]);
  
  const updateUrl = React.useCallback((field: string, val: string) => {
    if (!props.limits)
      return;
    
    let limits = copyProjectLimits(props.limits);
    const fields =	limits.welcome.fields
    				?	limits.welcome.fields
    				:	[];
    
    limits.welcome.fields = setField(fields, field, val);
    props.setLimits(limits);
  }, [props.project, props.limits]);
  
  console.log("ProjectTheme/themeVal=" + themeVal + "/message=" + message);
  
  const updateMessage = React.useCallback((val: string) => {
    if (props.room) {
      if (!props.setRoom)
        return;
        
      let room = copyProjectRoom(props.room);
      if (!room.fields)
        room.fields = [];
      
      room.fields = setField(room.fields, "theme-message", val);
      
      props.setRoom(room);
      console.log("ProjectTheme/updateMessage/val=" + val, "/room=", room);
      return;
    }
    
    if (!props.limits)
      return;
    
    let limits = copyProjectLimits(props.limits);
    limits.welcome.message = val;
    
    props.setLimits(limits);
    console.log("ProjectTheme/updateMessage/val=" + val, "/limits=", limits);
  }, [props.limits, props.room]);
  
  const updateDescription = React.useCallback((val: string) => {
    if (props.room) {
      if (!props.setRoom)
        return;
        
      let room = copyProjectRoom(props.room);
      if (!room.fields)
        room.fields = [];
      
      room.fields = setField(room.fields, "theme-description", val);
      
      props.setRoom(room);
      console.log("ProjectTheme/updateDescription/val=" + val, "/room=", room);
      return;
    }
    
    if (!props.limits)
      return;
    
    let limits = copyProjectLimits(props.limits);
    limits.welcome.description = val;
    
    props.setLimits(limits);
    console.log("ProjectTheme/updateDescription/val=" + val, "/limits=", limits);
  }, [props.limits, props.room]);
  
  const updateFooter = React.useCallback((val: string) => {
    if (props.room) {
      if (!props.setRoom)
        return;
        
      let room = copyProjectRoom(props.room);
      if (!room.fields)
        room.fields = [];
      
      room.fields = setField(room.fields, "theme-footer", val);
      
      props.setRoom(room);
      console.log("ProjectTheme/updateFooter/val=" + val, "/room=", room);
      return;
    }
    
    if (!props.limits)
      return;
    
    let limits = copyProjectLimits(props.limits);
    limits.welcome.footer = val;
    
    props.setLimits(limits);
    console.log("ProjectTheme/updateFooter/val=" + val, "/limits=", limits);
  }, [props.limits, props.room]);
  
  if (props.project.is_new)
    return null;
  
  const simple = themeVal == "|" || themeVal == "default";
  const rectangle = themeVal == "two|two-two" || themeVal == "two|two-three";
  
  const hotline =	props.limits &&
					props.limits.welcome &&
					props.limits.welcome.fields
					?	getField(props.limits.welcome.fields, "hotline")
					:	"";
  
  return (
  <FlexRow direction="column"
           align="start"
           className="py-0 gap-[16px] max-w-[448px]">
    <CatHeader caption="projects:project:theme:caption" />
    <Dropdown label={t('projects:project:theme:select_theme:caption')}
              isLoaded={!loading}
              isDisabled={seeAllOnly}
              value={themeVal}
              options={themeOpts}
              setValue={updateTheme}>
      <>
	  </>
    </Dropdown>
  {simple &&
    <React.Fragment>
      <TextArea value={message}
                isLoaded={!loading}
                isReadOnly={seeAllOnly}
                onValueChange={updateMessage}
                label={t("projects:project:theme:message:caption")}
                placeholder={t("projects:project:theme:message:placeholder")}
                maxChars={200} />
      <TextArea value={description}
                isLoaded={!loading}
                isReadOnly={seeAllOnly}
                onValueChange={updateDescription}
                label={t("projects:project:theme:description:caption")}
                placeholder={t("projects:project:theme:description:placeholder")}
                maxChars={200} />
      <TextArea value={footer}
                isLoaded={!loading}
                isReadOnly={seeAllOnly}
                onValueChange={updateFooter}
                label={t("projects:project:theme:footer:caption")}
                placeholder={t("projects:project:theme:footer:placeholder")}
                maxChars={200} />
    </React.Fragment>
  }
  {rectangle && !props.room &&
    <React.Fragment>
      <Input label={t('projects:project:theme:hotline:caption')}
             value={hotline}
             isLoaded={!loading}
             isReadOnly={seeAllOnly}
             isInvalid={hotline.length > 20}
             errorMessage={hotline.length > 20 ? t('projects:project:theme:hotline:error') : undefined}
             onValueChange={(val: string) => updateUrl("hotline", val)} />
    </React.Fragment>
  }
  </FlexRow>
  );
}

export default ProjectTheme;




